import { motion, useInView } from 'framer-motion';
import { useRef } from 'react';
import { CheckCircle, ArrowRight, Star, Sparkles } from 'lucide-react';
import { Button } from '../../components/ui/button';

const CALENDLY_URL = "https://calendly.com/romain-trallero/diagnostic-transition-digitale-60min";

// 3 forfaits uniquement : Mi-Temps, Dedie, Croissance
const plans = [
  {
    name: 'Assistant Mi-Temps',
    price: '499',
    description: 'CRM complet + Assistant humain a mi-temps pour toi aider au quotidien.',
    features: [
      'CRM batiment complet',
      'Pipeline commercial & chantier',
      'Gestion devis et factures',
      'Tous les agents IA inclus',
      'Assistant humain 20h/semaine',
      'Gestion des emails',
      'Rappels et relances',
      'Support prioritaire',
      'Formation personnalisee',
    ],
    popular: false,
    cta: 'Choisir cette formule',
  },
  {
    name: 'Assistant Dedie',
    price: '949',
    description: 'La formule complete pour les artisans ambitieux qui veulent se liberer.',
    features: [
      'Tout ce qui est dans Mi-Temps',
      'Assistant mi-temps ou plein temps',
      'Standard telephonique IA 24/7',
      'Marketing reseaux sociaux',
      'Gestion Instagram & Facebook',
      'SEO et blog automatise',
      'Assistant administratif complet',
      'Reporting mensuel',
    ],
    popular: true,
    cta: 'Choisir cette formule',
  },
  {
    name: 'Croissance',
    price: '1499',
    description: 'Pour développer activement ta clientèle et accelerer ta croissance.',
    features: [
      'Tout ce qui est dans Dedie',
      'Deux assistants dedies',
      'Assistant commercial dedie',
      'Prospection active LinkedIn',
      'Machine a nouveaux clients',
      'Recherche et qualification de prospects',
      'SaaS de pilotage commercial',
      'Strategie SEO avancee',
      'Accompagnement croissance',
    ],
    popular: false,
    cta: 'Choisir cette formule',
  },
];

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15,
      delayChildren: 0.2,
    },
  },
};

const cardVariants = {
  hidden: { 
    opacity: 0, 
    y: 50,
    scale: 0.9,
  },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      duration: 0.6,
      ease: [0.22, 1, 0.36, 1],
    },
  },
};

export default function PricingSection() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section 
      ref={ref}
      className="section-premium bg-slate-50"
      data-testid="pricing-section"
    >
      <div className="container mx-auto px-4 md:px-6 max-w-6xl">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          className="text-center mb-16 md:mb-20"
        >
          <motion.span 
            initial={{ opacity: 0, scale: 0.8 }}
            animate={isInView ? { opacity: 1, scale: 1 } : {}}
            transition={{ delay: 0.1, duration: 0.4 }}
            className="inline-flex items-center gap-2 px-4 py-2 bg-ba-secondary/10 text-ba-secondary text-xs font-semibold uppercase tracking-wider rounded-full mb-6"
          >
            <Sparkles className="w-4 h-4" />
            Tarifs transparents
          </motion.span>
          <h2 className="heading-section mb-6">
            Choisis ton{' '}
            <span className="text-gradient">niveau d'accompagnement</span>
          </h2>
          <p className="body-large max-w-3xl mx-auto">
            Trois formules avec assistant humain pour toi liberer du temps 
            et développer ton activité. Sans engagement.
          </p>
        </motion.div>

        {/* Pricing Cards - 3 colonnes */}
        <motion.div 
          variants={containerVariants}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
          className="grid md:grid-cols-3 gap-6 md:gap-8"
        >
          {plans.map((plan, index) => (
            <motion.div
              key={index}
              variants={cardVariants}
              whileHover={{ 
                y: plan.popular ? -12 : -8,
                transition: { duration: 0.3 }
              }}
              className={`relative rounded-3xl p-6 md:p-8 transition-shadow duration-300 ${
                plan.popular 
                  ? 'bg-white border-2 border-ba-secondary shadow-2xl scale-[1.02] md:scale-105' 
                  : 'bg-white border border-slate-200 hover:shadow-xl'
              }`}
            >
              {/* Popular badge with animation */}
              {plan.popular && (
                <motion.div 
                  initial={{ opacity: 0, y: -20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5, duration: 0.4, type: "spring" }}
                  className="absolute -top-4 left-1/2 -translate-x-1/2"
                >
                  <motion.span 
                    animate={{ 
                      boxShadow: [
                        "0 4px 14px rgba(249, 115, 22, 0.3)",
                        "0 4px 25px rgba(249, 115, 22, 0.5)",
                        "0 4px 14px rgba(249, 115, 22, 0.3)",
                      ]
                    }}
                    transition={{ duration: 2, repeat: Infinity }}
                    className="inline-flex items-center gap-1.5 bg-ba-secondary text-white px-5 py-2 rounded-full text-sm font-bold uppercase tracking-wide"
                  >
                    <Star className="w-4 h-4 fill-current" />
                    Recommande
                  </motion.span>
                </motion.div>
              )}
              
              {/* Plan name */}
              <div className="mb-6 mt-2">
                <h3 className="text-xl md:text-2xl font-bold text-slate-800 mb-2">
                  {plan.name}
                </h3>
                <p className="text-slate-500 text-sm leading-relaxed">
                  {plan.description}
                </p>
              </div>

              {/* Price with counter effect */}
              <div className="mb-8">
                <div className="flex items-baseline gap-1">
                  <motion.span 
                    initial={{ opacity: 0, scale: 0.5 }}
                    animate={isInView ? { opacity: 1, scale: 1 } : {}}
                    transition={{ delay: 0.3 + index * 0.1, type: "spring", stiffness: 200 }}
                    className="text-4xl md:text-5xl font-bold text-slate-800"
                  >
                    {plan.price}€
                  </motion.span>
                  <span className="text-slate-400 text-base font-medium">/ mois</span>
                </div>
              </div>

              {/* Features with staggered checkmarks */}
              <ul className="space-y-3 mb-8">
                {plan.features.map((feature, featureIndex) => (
                  <motion.li 
                    key={featureIndex} 
                    initial={{ opacity: 0, x: -10 }}
                    animate={isInView ? { opacity: 1, x: 0 } : {}}
                    transition={{ delay: 0.4 + index * 0.1 + featureIndex * 0.04 }}
                    className="flex items-start gap-3 group"
                  >
                    <motion.div
                      whileHover={{ scale: 1.2 }}
                      transition={{ type: "spring", stiffness: 400 }}
                    >
                      <CheckCircle className={`w-5 h-5 flex-shrink-0 mt-0.5 ${plan.popular ? 'text-ba-secondary' : 'text-ba-success'}`} />
                    </motion.div>
                    <span className="text-slate-600 text-sm leading-relaxed group-hover:text-slate-800 transition-colors">
                      {feature}
                    </span>
                  </motion.li>
                ))}
              </ul>

              {/* CTA */}
              <motion.a 
                href={CALENDLY_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="block"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <Button 
                  className={`w-full rounded-full py-6 text-base font-semibold transition-all group ${
                    plan.popular 
                      ? 'bg-ba-secondary hover:bg-ba-secondary/90 text-white shadow-lg hover:shadow-xl btn-glow-pulse' 
                      : 'bg-slate-800 hover:bg-slate-700 text-white'
                  }`}
                  data-testid={`pricing-cta-${plan.name.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  {plan.cta}
                  <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                </Button>
              </motion.a>
            </motion.div>
          ))}
        </motion.div>

        {/* Note */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={isInView ? { opacity: 1 } : {}}
          transition={{ delay: 0.8, duration: 0.5 }}
          className="text-center mt-12"
        >
          <p className="text-slate-500 text-sm">
            Le service demarre 15 jours apres le paiement • Sans engagement longue duree • Frais de mise en place : 500€
          </p>
          <p className="text-slate-400 text-xs mt-2">
            Tous les forfaits incluent le CRM complet et les agents IA
          </p>
        </motion.div>
      </div>
    </section>
  );
}
